import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import type { WhatsAppServer } from './api/whatsapp-servers.service';
import { useWhatsAppServersListQuery, useWhatsAppServersStatsQuery } from './hooks/useWhatsAppServersDataQuery';
import { useWhatsAppServerMutations } from './hooks/useWhatsAppServerMutations';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { ConfirmDialog } from '@/design-system';
import { WhatsAppServerFormDialog } from './components/WhatsAppServerFormDialog';
import { WhatsAppServerStatsCards } from './components/WhatsAppServerStatsCards';
import { WhatsAppServerTable, type WhatsAppServerTableHandlers } from './components/WhatsAppServerTable';

interface WhatsAppServersPageProps {
  embedded?: boolean;
}

export function WhatsAppServersPage({ embedded }: WhatsAppServersPageProps = {}) {
  const onErrorToast = useErrorToast();
  const { createServer, updateServer, deleteServer } =
    useWhatsAppServerMutations();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedServer, setSelectedServer] = useState<WhatsAppServer | null>(null);

  const serversQuery = useWhatsAppServersListQuery();
  const statsQuery = useWhatsAppServersStatsQuery();
  const servers = serversQuery.data ?? [];
  const stats = statsQuery.data ?? null;
  const isLoading = serversQuery.isLoading || statsQuery.isLoading;

  useEffect(() => {
    const err = serversQuery.error ?? statsQuery.error;
    if ((serversQuery.isError || statsQuery.isError) && err) {
      onErrorToast(err, 'Erro ao carregar servidores');
    }
  }, [serversQuery.isError, serversQuery.error, statsQuery.isError, statsQuery.error, onErrorToast]);

  const tableHandlers = useMemo<WhatsAppServerTableHandlers>(
    () => ({
      onEdit: (s) => {
        setSelectedServer(s);
        setShowEditDialog(true);
      },
      onDelete: (s) => {
        setSelectedServer(s);
        setShowDeleteDialog(true);
      },
      onCreate: () => setShowCreateDialog(true),
    }),
    []
  );

  const content = (
    <>
      {stats ? <WhatsAppServerStatsCards stats={stats} /> : null}

      <WhatsAppServerTable
        servers={servers}
        loading={isLoading}
        handlers={tableHandlers}
      />

      <WhatsAppServerFormDialog
        mode="create"
        isOpen={showCreateDialog}
        server={null}
        onClose={() => setShowCreateDialog(false)}
        createServer={createServer}
        updateServer={updateServer}
      />

      <WhatsAppServerFormDialog
        mode="edit"
        isOpen={showEditDialog}
        server={selectedServer}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedServer(null);
        }}
        createServer={createServer}
        updateServer={updateServer}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedServer(null);
        }}
        onConfirm={() => {
          if (!selectedServer) return;
          void deleteServer.mutateAsync(selectedServer.id).then(() => {
            setShowDeleteDialog(false);
            setSelectedServer(null);
          });
        }}
        title="Deletar Servidor"
        message={`Tem certeza que deseja deletar o servidor "${selectedServer?.name}"?${
          selectedServer && selectedServer.currentLoad > 0
            ? `\n\n⚠️ ATENÇÃO: Este servidor possui ${selectedServer.currentLoad} instâncias ativas.`
            : ''
        }`}
        confirmText="Deletar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteServer.isPending}
      />
    </>
  );

  if (embedded) return content;
  return <DashboardLayout title="Servidores WhatsApp (UAZAPI)">{content}</DashboardLayout>;
}
